import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box
} from '@mui/material';
import {
  CURRENCY_LOCALES,
  CURRENCY_CONFIG
} from '../../config/currency';
import {
  getCurrentCurrencyLocale,
  setCurrencyLocale,
  useCurrencyLocaleListener
} from '../../utils/CurrencyUtils';
import commonActions from '../../redux/reducers/common/actions';

const CurrencySelector = ({
  variant = 'outlined',
  size = 'medium',
  label = 'Currency',
  fullWidth = false,
  disabled = false,
  showDescription = true,
  sx = {}
}) => {
  // EGP Branch: Always disable currency selection and force EGP
  const isEGPBranch = process.env.REACT_APP_LOCALE === 'en-EG';
  const forceDisabled = isEGPBranch || disabled;
  const dispatch = useDispatch();
  const reduxCurrencyLocale = useSelector(state => state.common?.currencyLocale);
  const [currentLocale, setCurrentLocale] = useState(getCurrentCurrencyLocale());

  // Force sync with detected currency on mount
  useEffect(() => {
    // EGP Branch: Force EGP currency
    const detectedCurrency = isEGPBranch ? 'EGP' : getCurrentCurrencyLocale();
    console.log('🎯 CurrencySelector: Force syncing with detected currency:', detectedCurrency);

    if (detectedCurrency !== currentLocale) {
      setCurrentLocale(detectedCurrency);
      // Also update Redux to keep it in sync
      dispatch(commonActions.setCurrencyLocale(detectedCurrency));
    }
  }, [isEGPBranch]); // Run only on mount and when branch changes

  // Listen for currency locale changes from localStorage or other sources
  useCurrencyLocaleListener((newLocale) => {
    console.log('🎯 CurrencySelector: Currency changed via listener:', newLocale);
    setCurrentLocale(newLocale);
  });

  // Sync with Redux state
  useEffect(() => {
    console.log('🎯 CurrencySelector: Redux state changed:', {
      reduxCurrencyLocale,
      currentLocale,
      shouldUpdate: reduxCurrencyLocale && reduxCurrencyLocale !== currentLocale
    });

    if (reduxCurrencyLocale && reduxCurrencyLocale !== currentLocale) {
      console.log('🎯 CurrencySelector: Updating from Redux:', reduxCurrencyLocale);
      setCurrentLocale(reduxCurrencyLocale);
    }
  }, [reduxCurrencyLocale, currentLocale]);

  const handleChange = (event) => {
    // EGP Branch: Prevent currency changes
    if (isEGPBranch) {
      console.log('🎯 CurrencySelector: Currency change blocked - EGP branch is locked to EGP');
      return;
    }

    const newLocale = event.target.value;

    console.log('🎯 CurrencySelector: User selected currency:', newLocale);

    // Update localStorage
    setCurrencyLocale(newLocale);

    // Update Redux state
    dispatch(commonActions.setCurrencyLocale(newLocale));

    // Update local state
    setCurrentLocale(newLocale);

    console.log('🎯 CurrencySelector: Currency updated to:', newLocale);
  };

  const getCurrencyDisplayText = (locale) => {
    const config = CURRENCY_CONFIG[locale];
    return `${config.name} (${config.code})`;
  };

  const getCurrencyDescription = (locale) => {
    const config = CURRENCY_CONFIG[locale];
    if (locale === CURRENCY_LOCALES.EGP) {
      return 'Display amounts with "EGP" text instead of Dhiram icons';
    }
    return 'Display amounts with Dhiram icons';
  };

  return (
    <Box sx={sx}>
      <FormControl
        variant={variant}
        size={size}
        fullWidth={fullWidth}
        disabled={forceDisabled}
      >
        <InputLabel id="currency-selector-label">{label}</InputLabel>
        <Select
          labelId="currency-selector-label"
          id="currency-selector"
          value={currentLocale}
          label={label}
          onChange={handleChange}
        >
          {Object.values(CURRENCY_LOCALES).map((locale) => (
            <MenuItem key={locale} value={locale}>
              <Box>
                <Typography variant="body1">
                  {getCurrencyDisplayText(locale)}
                </Typography>
                {showDescription && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    {getCurrencyDescription(locale)}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default CurrencySelector;
